// src/components/home/PostEditor.jsx
import React, {
  useState, useRef, useEffect, useMemo, useCallback,
} from 'react';
import { db, storage } from '../../firebase';
import {
  doc, updateDoc, deleteDoc, serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { useAuth } from '../../context/AuthContext';
import {
  validateMedia,
  uploadMedia,
  isVideoFile,
  POST_BG_PRESETS,
  normalizeMedia,
  FONT_FAMILIES,
  FONT_SIZES,
  FONT_WEIGHTS,
  TEXT_ALIGNS,
  DEFAULT_FONT,
  getFontClass,
} from '../../utils/feedHelpers';
import { sanitizeHtml, htmlToPlainText } from '../../utils/sanitizeHtml';
import { useRichEditor } from '../../hooks/useRichEditor';
import RichTextToolbar from './RichTextToolbar';

/* ================================================================== */
/* Visibility options                                                  */
/* ================================================================== */

const VISIBILITY_OPTIONS = [
  {
    value: 'public',
    label: 'Public',
    icon: 'fa-globe',
    hint: 'Anyone can see this',
    activeCls:
      'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300 font-semibold',
    inactiveCls:
      'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700',
  },
  {
    value: 'private',
    label: 'Members',
    icon: 'fa-users',
    hint: 'Only logged-in members',
    activeCls:
      'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 font-semibold',
    inactiveCls:
      'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700',
  },
  {
    value: 'onlyMe',
    label: 'Only Me',
    icon: 'fa-lock',
    hint: 'Only you and admins',
    activeCls:
      'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-100 font-semibold',
    inactiveCls:
      'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700',
  },
];

const getVisibilityMeta = (value) =>
  VISIBILITY_OPTIONS.find((o) => o.value === value) || VISIBILITY_OPTIONS[0];

/* ================================================================== */
/* Helpers                                                             */
/* ================================================================== */

const storagePathFromUrl = (url) => {
  if (!url) return null;
  const m = url.match(/\/o\/([^?]+)/);
  return m ? decodeURIComponent(m[1]) : null;
};

const safeDeleteFromStorage = async (url) => {
  const path = storagePathFromUrl(url);
  if (!path) return;
  try {
    await deleteObject(ref(storage, path));
  } catch (err) {
    if (err?.code !== 'storage/object-not-found') {
      console.warn('Storage delete skipped:', path, err?.code || err);
    }
  }
};

const toLocalDateTimeValue = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
};

const buildInitialHtml = (post) => {
  if (post?.contentHtml) return sanitizeHtml(post.contentHtml);
  if (post?.content) {
    const escaped = String(post.content)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    return escaped
      .split(/\n{2,}/)
      .map((block) =>
        block
          .split('\n')
          .map((line) => line)
          .join('<br>'),
      )
      .map((block) => `<p>${block || '<br>'}</p>`)
      .join('');
  }
  return '';
};

const COLLAPSE_THRESHOLD = 12;

/* ================================================================== */
/* MediaTile — reusable preview tile                                   */
/* ================================================================== */

const MediaTile = ({ item, isCover, onSetCover, onRemove, disabled }) => (
  <div
    className={`relative group rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 ${
      isCover ? 'ring-2 ring-orange' : ''
    }`}
  >
    <button
      type="button"
      onClick={onSetCover}
      disabled={disabled}
      className="block w-full active:opacity-80 transition-opacity"
      title="Tap to set as cover"
      aria-label={isCover ? 'Current cover' : 'Set as cover'}
    >
      {item.type === 'video' ? (
        <video
          src={item.url}
          className="w-full h-24 object-cover"
          muted
          playsInline
          preload="metadata"
        />
      ) : (
        <img
          src={item.url}
          alt=""
          className="w-full h-24 object-cover"
          loading="lazy"
        />
      )}
    </button>
    {item.type === 'video' && (
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center">
          <i className="fas fa-play text-white text-xs" aria-hidden="true" />
        </div>
      </div>
    )}
    {isCover && (
      <span className="absolute top-1 left-1 bg-orange text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-1 shadow">
        <i className="fas fa-star text-[8px]" aria-hidden="true" />
        Cover
      </span>
    )}
    <button
      type="button"
      onClick={onRemove}
      disabled={disabled}
      aria-label="Remove"
      className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 transition disabled:opacity-50 shadow-md z-10 active:scale-90"
    >
      <i className="fas fa-times text-xs" aria-hidden="true" />
    </button>
  </div>
);

/* ================================================================== */
/* PostEditor                                                          */
/* ================================================================== */

const PostEditor = ({ post, onClose, onUpdated, onDeleted }) => {
  const { isAdmin } = useAuth();

  const initialMedia = useMemo(() => normalizeMedia(post), [post]);

  /* -------------------- Media state -------------------- */
  const [items, setItems] = useState(() =>
    initialMedia.map((m) => ({
      url: m.url,
      type: m.type,
      isCover: m.isCover,
    })),
  );
  const [newFiles, setNewFiles] = useState([]);
  const [newPreviews, setNewPreviews] = useState([]);
  const [coverKey, setCoverKey] = useState(() => {
    const idx = initialMedia.findIndex((m) => m.isCover);
    return idx >= 0 ? `e-${idx}` : initialMedia.length > 0 ? 'e-0' : null;
  });

  const [existingExpanded, setExistingExpanded] = useState(
    () => initialMedia.length <= COLLAPSE_THRESHOLD,
  );
  const [newExpanded, setNewExpanded] = useState(false);

  /* -------------------- Design state -------------------- */
  const [bgPreset, setBgPreset] = useState(post.design?.bgPreset || 'default');
  const [font, setFont] = useState(post.design?.font || DEFAULT_FONT);
  const [designTab, setDesignTab] = useState('bg');
  const [visibility, setVisibility] = useState(post.visibility || 'public');
  const [showDesignPanel, setShowDesignPanel] = useState(false);

  /* -------------------- Admin date state -------------------- */
  const [postDate, setPostDate] = useState(() => {
    const d = post.postDate?.toDate?.();
    return d ? toLocalDateTimeValue(d) : '';
  });
  const [showDatePanel, setShowDatePanel] = useState(false);

  /* -------------------- Submit state -------------------- */
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  /* -------------------- Link dialog -------------------- */
  const [linkDialog, setLinkDialog] = useState(null);

  /* -------------------- Rich editor -------------------- */
  const editor = useRichEditor();
  const [plainText, setPlainText] = useState('');

  const handleEditorInput = useCallback(() => {
    const html = editor.editorRef.current?.innerHTML || '';
    setPlainText(htmlToPlainText(html));
  }, [editor.editorRef]);

  /* -------------------- Refs -------------------- */
  const previewUrlsRef = useRef([]);
  const removedExistingRef = useRef([]);
  const busyRef = useRef(false);
  const initializedRef = useRef(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  /* -------------------- Effects -------------------- */

  // Seed the contentEditable region once on mount
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    const html = buildInitialHtml(post);
    if (editor.editorRef.current) {
      editor.editorRef.current.innerHTML = html;
    }
    setPlainText(htmlToPlainText(html));
  }, [post, editor.editorRef]);

  // Revoke object URLs on unmount
  useEffect(() => {
    return () => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      previewUrlsRef.current = [];
    };
  }, []);

  // Reset delete-confirm state if the editor is reused for another post
  useEffect(() => {
    setConfirmDelete(false);
  }, [post.id]);

  // Escape key closes
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (linkDialog) {
        setLinkDialog(null);
        return;
      }
      if (!busyRef.current) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, linkDialog]);

  useEffect(() => {
    busyRef.current = busy;
  }, [busy]);

  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  /* -------------------- Derived -------------------- */
  const activeBg =
    POST_BG_PRESETS.find((p) => p.id === bgPreset) || POST_BG_PRESETS[0];
  const fontClass = getFontClass(font);
  const hasBg = bgPreset !== 'default';

  const fontAlignBox =
    font?.align === 'left'
      ? 'justify-start text-left'
      : font?.align === 'right'
      ? 'justify-end text-right'
      : 'justify-center text-center';

  const totalMedia = items.length + newFiles.length;
  const canSave = !busy && (plainText.trim() || totalMedia > 0);

  const existingVisible =
    existingExpanded || items.length <= COLLAPSE_THRESHOLD
      ? items
      : items.slice(0, COLLAPSE_THRESHOLD);

  const newVisible =
    newExpanded || newPreviews.length <= COLLAPSE_THRESHOLD
      ? newPreviews
      : newPreviews.slice(0, COLLAPSE_THRESHOLD);

  const quickDates = useMemo(
    () => [
      { label: 'Now', value: toLocalDateTimeValue(new Date()) },
      {
        label: '1h ago',
        value: toLocalDateTimeValue(new Date(Date.now() - 60 * 60 * 1000)),
      },
      {
        label: 'Yesterday',
        value: toLocalDateTimeValue(new Date(Date.now() - 24 * 60 * 60 * 1000)),
      },
      {
        label: '1w ago',
        value: toLocalDateTimeValue(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)),
      },
      {
        label: '1m ago',
        value: toLocalDateTimeValue(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)),
      },
    ],
    [],
  );

  const visibilityMeta = getVisibilityMeta(visibility);

  /* -------------------- Cover helpers -------------------- */

  const recomputeCoverKey = useCallback(
    (nextItems, nextNewFiles) => {
      if (nextItems.length > 0) return 'e-0';
      if (nextNewFiles.length > 0) return 'n-0';
      return null;
    },
    [],
  );

  /* -------------------- File handling -------------------- */

  const handleFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (!picked.length) return;

    const errors = [];
    const accepted = [];
    for (const f of picked) {
      const err = validateMedia(f);
      if (err) errors.push(err);
      else accepted.push(f);
    }
    setError(errors.length ? errors.join('\n') : '');

    if (!accepted.length) return;

    const nextFiles = [...newFiles, ...accepted];

    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    const nextPreviews = nextFiles.map((f) => ({
      url: URL.createObjectURL(f),
      type: isVideoFile(f) ? 'video' : 'image',
    }));
    previewUrlsRef.current = nextPreviews.map((p) => p.url);

    setNewFiles(nextFiles);
    setNewPreviews(nextPreviews);

    if (!coverKey && nextFiles.length > 0) {
      setCoverKey('n-0');
    }
    if (nextPreviews.length <= COLLAPSE_THRESHOLD) {
      setNewExpanded(true);
    }
  };

  const removeNewFile = (idx) => {
    const url = newPreviews[idx]?.url;
    if (url) URL.revokeObjectURL(url);
    previewUrlsRef.current = previewUrlsRef.current.filter((u) => u !== url);

    const nextFiles = newFiles.filter((_, i) => i !== idx);
    const nextPreviews = newPreviews.filter((_, i) => i !== idx);

    const wasCover = coverKey === `n-${idx}`;
    if (wasCover) {
      setCoverKey(recomputeCoverKey(items, nextFiles));
    } else if (coverKey?.startsWith('n-')) {
      const coverIdx = Number(coverKey.slice(2));
      if (coverIdx > idx) setCoverKey(`n-${coverIdx - 1}`);
    }

    setNewFiles(nextFiles);
    setNewPreviews(nextPreviews);
  };

  const removeExistingItem = (idx) => {
    const removed = items[idx];
    removedExistingRef.current.push(removed);

    const nextItems = items.filter((_, i) => i !== idx);

    const wasCover = coverKey === `e-${idx}`;
    if (wasCover) {
      setCoverKey(recomputeCoverKey(nextItems, newFiles));
    } else if (coverKey?.startsWith('e-')) {
      const coverIdx = Number(coverKey.slice(2));
      if (coverIdx > idx) setCoverKey(`e-${coverIdx - 1}`);
    }

    setItems(nextItems);
  };

  /* -------------------- Link dialog -------------------- */

  const requestLink = () => {
    const sel = window.getSelection();
    const hasSelection =
      !!sel && sel.rangeCount > 0 && !sel.getRangeAt(0).collapsed;
    setLinkDialog({ url: '', hasSelection });
  };

  const confirmLink = () => {
    if (!linkDialog) return;
    const { url } = linkDialog;
    if (!url.trim()) {
      editor.exec('unlink');
    } else {
      editor.insertLink(url);
    }
    setLinkDialog(null);
    handleEditorInput();
  };

  /* -------------------- Save -------------------- */
  const handleSave = async () => {
    if (busyRef.current) return;

    const rawHtml = editor.editorRef.current?.innerHTML || '';
    const cleanHtml = sanitizeHtml(rawHtml);
    const plain = htmlToPlainText(cleanHtml).trim();

    if (!plain && totalMedia === 0) {
      setError('Post must have text or media.');
      return;
    }

    busyRef.current = true;
    setBusy(true);
    setError('');
    setProgress({ done: 0, total: newFiles.length });

    try {
      const uploaded = [];
      for (let i = 0; i < newFiles.length; i++) {
        const file = newFiles[i];
        const url = await uploadMedia(file, 'posts');
        uploaded.push({
          url,
          type: isVideoFile(file) ? 'video' : 'image',
          isCover: false,
          key: `n-${i}`,
        });
        setProgress({ done: i + 1, total: newFiles.length });
      }

      for (const removed of removedExistingRef.current) {
        if (removed?.url) {
          try {
            await safeDeleteFromStorage(removed.url);
          } catch (err) {
            console.warn('Could not delete old item', err);
          }
        }
      }

      const existingWithKeys = items.map((item, i) => ({
        ...item,
        key: `e-${i}`,
      }));
      const combined = [...existingWithKeys, ...uploaded];

      const finalMedia = combined.map((item) => ({
        url: item.url,
        type: item.type,
        isCover: coverKey ? item.key === coverKey : false,
      }));

      if (!finalMedia.some((m) => m.isCover) && finalMedia.length) {
        finalMedia[0].isCover = true;
      }

      const wasApproved = post.approved === true;
      const needsReapproval = !isAdmin && wasApproved;

      const updates = {
        content: plain,
        contentHtml: cleanHtml,
        media: finalMedia,
        photos: finalMedia.filter((m) => m.type === 'image').map((m) => m.url),
        videos: finalMedia.filter((m) => m.type === 'video').map((m) => m.url),
        design: {
          bgPreset: hasBg ? bgPreset : null,
          font: hasBg ? font : null,
        },
        visibility,
        updatedAt: serverTimestamp(),
        editedAt: serverTimestamp(),
      };

      if (isAdmin && postDate) {
        const d = new Date(postDate);
        if (!isNaN(d.getTime())) {
          const newTs = Timestamp.fromDate(d);
          const originalTs = post.postDate?.toDate?.() ?? null;
          const changed =
            !originalTs || Math.abs(originalTs.getTime() - d.getTime()) > 60_000;
          updates.postDate = newTs;
          if (changed) updates.backdated = true;
        }
      }

      if (needsReapproval) {
        updates.approved = false;
        updates.approvalStatus = 'pending';
        updates.approvedBy = null;
        updates.approvedAt = null;
      }

      await updateDoc(doc(db, 'posts', post.id), updates);
      onUpdated?.(updates);
      onClose();
    } catch (err) {
      console.error('Post update failed:', err);
      setError(err.message || 'Failed to update post.');
    } finally {
      setBusy(false);
      busyRef.current = false;
    }
  };

  /* -------------------- Delete -------------------- */
  const handleDelete = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      const media = normalizeMedia(post);
      for (const m of media) {
        await safeDeleteFromStorage(m.url);
      }
      await deleteDoc(doc(db, 'posts', post.id));
      onDeleted?.(post.id);
      onClose();
    } catch (err) {
      console.error('Post delete failed:', err);
      setError(err.message || 'Failed to delete post.');
    } finally {
      setBusy(false);
      busyRef.current = false;
    }
  };

  /* -------------------- Reset design -------------------- */
  const resetDesign = () => {
    setBgPreset('default');
    setFont(DEFAULT_FONT);
  };

  /* -------------------- Live preview -------------------- */
  const livePreviewHtml = useMemo(() => {
    const raw = editor.editorRef.current?.innerHTML || '';
    if (!raw) return '';
    return sanitizeHtml(raw);
  }, [plainText, editor.editorRef]);

  /* -------------------- Render -------------------- */

  return (
    <div
      className="fixed inset-0 z-[95] bg-black/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 overflow-y-auto"
      onClick={() => !busy && onClose?.()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="post-editor-title"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      <div
        className="bg-white dark:bg-gray-800 w-full rounded-2xl max-w-2xl max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        
        {/* Header */}
        <div className="flex justify-between items-center px-4 sm:px-5 py-3 border-b dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-2">
            <i className="fas fa-pen text-orange" aria-hidden="true" />
            <h3
              id="post-editor-title"
              className="text-base sm:text-lg font-bold text-gray-800 dark:text-white"
            >
              Edit Post
            </h3>
            {totalMedia > 0 && (
              <span className="text-[10px] text-gray-400 dark:text-gray-500">
                {totalMedia} item{totalMedia === 1 ? '' : 's'}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            className="w-9 h-9 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 disabled:opacity-50 flex items-center justify-center transition active:scale-90"
          >
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-5 overflow-y-auto flex-1 space-y-4 min-h-0">
          {/* Content editor */}
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              Content
            </label>
            <div
              className={`rounded-lg overflow-hidden border focus-within:ring-2 focus-within:ring-orange ${
                hasBg
                  ? `${activeBg.bg} ${activeBg.text} ${fontClass} border-transparent`
                  : 'dark:bg-gray-700 dark:border-gray-600 dark:text-white'
              }`}
            >
              <RichTextToolbar
                editor={editor}
                onRequestLink={requestLink}
                disabled={busy}
              />
              <div
                ref={editor.editorRef}
                contentEditable={!busy}
                suppressContentEditableWarning
                role="textbox"
                aria-multiline="true"
                aria-label="Post content"
                data-placeholder="What's on your mind?"
                onInput={handleEditorInput}
                onBlur={editor.saveSelection}
                onKeyUp={editor.saveSelection}
                onMouseUp={editor.saveSelection}
                className={`post-rich-editor min-h-[100px] max-h-[300px] overflow-y-auto p-3 text-sm leading-relaxed focus:outline-none ${
                  hasBg ? 'text-inherit' : 'text-gray-900 dark:text-white'
                }`}
                style={{ wordBreak: 'break-word' }}
              />
            </div>
            <p className="text-[10px] text-gray-400 text-right mt-0.5">
              {plainText.length}/2000
            </p>
          </div>

          {/* Design panel */}
          <div>
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setShowDesignPanel((s) => !s)}
                aria-expanded={showDesignPanel}
                className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-orange transition"
              >
                <i className="fas fa-palette text-purple-500" aria-hidden="true" />
                Design &amp; font
                <i
                  className={`fas fa-chevron-${showDesignPanel ? 'up' : 'down'} text-xs`}
                  aria-hidden="true"
                />
              </button>
              {hasBg && (
                <button
                  type="button"
                  onClick={resetDesign}
                  className="text-[11px] text-gray-500 hover:text-orange underline"
                >
                  Reset design
                </button>
              )}
            </div>

            {showDesignPanel && (
              <div className="mt-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 border dark:border-gray-700">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex gap-1 bg-white dark:bg-gray-800 rounded-lg p-1 border dark:border-gray-700">
                    <button
                      type="button"
                      onClick={() => setDesignTab('bg')}
                      className={`flex items-center gap-1.5 text-xs px-3 py-1 rounded-md transition ${
                        designTab === 'bg'
                          ? 'bg-orange text-white font-medium'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <i className="fas fa-palette text-[10px]" aria-hidden="true" />
                      Background
                    </button>
                    <button
                      type="button"
                      onClick={() => setDesignTab('font')}
                      className={`flex items-center gap-1.5 text-xs px-3 py-1 rounded-md transition ${
                        designTab === 'font'
                          ? 'bg-orange text-white font-medium'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <i className="fas fa-font text-[10px]" aria-hidden="true" />
                      Font
                    </button>
                  </div>
                </div>

                {designTab === 'bg' && (
                  <>
                    <div className="flex flex-wrap gap-2">
                      {POST_BG_PRESETS.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => setBgPreset(p.id)}
                          aria-label={p.label}
                          aria-pressed={bgPreset === p.id}
                          title={p.label}
                          className={`w-10 h-10 rounded-lg border-2 transition-all active:scale-95 ${
                            bgPreset === p.id
                              ? 'border-orange ring-2 ring-orange/30 scale-105'
                              : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                          } ${p.bg || 'bg-white dark:bg-gray-800'}`}
                        >
                          {p.id === 'default' && (
                            <i
                              className="fas fa-ban text-gray-400 text-xs"
                              aria-hidden="true"
                            />
                          )}
                        </button>
                      ))}
                    </div>
                    {bgPreset === 'default' && (
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-2">
                        Pick a background to unlock font styling.
                      </p>
                    )}
                  </>
                )}

                {designTab === 'font' && (
                  <>
                    {!hasBg ? (
                      <div className="text-center py-4 text-xs text-gray-500">
                        <i
                          className="fas fa-info-circle mb-1 block text-lg opacity-40"
                          aria-hidden="true"
                        />
                        Pick a background first to enable font styling.
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div>
                          <p className="text-[10px] uppercase font-semibold text-gray-500 mb-1.5">
                            Font family
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {FONT_FAMILIES.map((f) => (
                              <button
                                key={f.id}
                                type="button"
                                onClick={() => setFont({ ...font, family: f.id })}
                                className={`${f.className} text-xs px-2.5 py-1 rounded-md border transition active:scale-95 ${
                                  font.family === f.id
                                    ? 'bg-orange text-white border-orange'
                                    : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:border-orange'
                                }`}
                              >
                                {f.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <p className="text-[10px] uppercase font-semibold text-gray-500 mb-1.5">
                            Size
                          </p>
                          <div className="flex gap-1.5">
                            {FONT_SIZES.map((s) => (
                              <button
                                key={s.id}
                                type="button"
                                onClick={() => setFont({ ...font, size: s.id })}
                                className={`text-xs px-3 py-1 rounded-md border transition active:scale-95 ${
                                  font.size === s.id
                                    ? 'bg-orange text-white border-orange'
                                    : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:border-orange'
                                }`}
                              >
                                {s.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <p className="text-[10px] uppercase font-semibold text-gray-500 mb-1.5">
                            Weight
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {FONT_WEIGHTS.map((w) => (
                              <button
                                key={w.id}
                                type="button"
                                onClick={() => setFont({ ...font, weight: w.id })}
                                className={`${w.className} text-xs px-2.5 py-1 rounded-md border transition active:scale-95 ${
                                  font.weight === w.id
                                    ? 'bg-orange text-white border-orange'
                                    : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:border-orange'
                                }`}
                              >
                                {w.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <p className="text-[10px] uppercase font-semibold text-gray-500 mb-1.5">
                              Style
                            </p>
                            <button
                              type="button"
                              onClick={() => setFont({ ...font, italic: !font.italic })}
                              aria-pressed={!!font.italic}
                              className={`italic text-xs px-3 py-1 rounded-md border transition w-full active:scale-95 ${
                                font.italic
                                  ? 'bg-orange text-white border-orange'
                                  : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:border-orange'
                              }`}
                            >
                              Italic
                            </button>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase font-semibold text-gray-500 mb-1.5">
                              Align
                            </p>
                            <div className="flex gap-1">
                              {TEXT_ALIGNS.map((a) => (
                                <button
                                  key={a.id}
                                  type="button"
                                  onClick={() => setFont({ ...font, align: a.id })}
                                  aria-label={a.label}
                                  aria-pressed={font.align === a.id}
                                  title={a.label}
                                  className={`flex-1 text-xs py-1 rounded-md border transition active:scale-95 ${
                                    font.align === a.id
                                      ? 'bg-orange text-white border-orange'
                                      : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:border-orange'
                                  }`}
                                >
                                  <i
                                    className={`fas ${a.icon} text-[11px]`}
                                    aria-hidden="true"
                                  />
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => setFont(DEFAULT_FONT)}
                          className="text-[11px] text-gray-500 hover:text-orange underline"
                        >
                          Reset font to default
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Live preview */}
          {plainText.trim() && (
            <div className="rounded-lg overflow-hidden border dark:border-gray-700">
              <div className="bg-gray-100 dark:bg-gray-700/50 px-3 py-1.5 text-[10px] uppercase font-semibold text-gray-500 flex items-center gap-1.5">
                <i className="fas fa-eye text-[10px]" aria-hidden="true" />
                Live preview
              </div>
              <div className="p-3">
                {hasBg ? (
                  <div
                    className={`rounded-xl px-4 py-5 min-h-[80px] flex ${fontAlignBox} ${activeBg.bg} ${activeBg.text}`}
                  >
                    <div
                      className={`${fontClass} whitespace-pre-wrap break-words w-full post-rich-content`}
                      dangerouslySetInnerHTML={{ __html: livePreviewHtml }}
                    />
                  </div>
                ) : (
                  <div
                    className="text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap break-words post-rich-content"
                    dangerouslySetInnerHTML={{ __html: livePreviewHtml }}
                  />
                )}
              </div>
            </div>
          )}

          {/* Admin date panel */}
          {isAdmin && (
            <div>
              <button
                type="button"
                onClick={() => setShowDatePanel((s) => !s)}
                aria-expanded={showDatePanel}
                className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-orange transition"
              >
                <i className="fas fa-clock text-orange" aria-hidden="true" />
                Post date
                <i
                  className={`fas fa-chevron-${showDatePanel ? 'up' : 'down'} text-xs`}
                  aria-hidden="true"
                />
              </button>

              {showDatePanel && (
                <div className="mt-2 p-3 rounded-lg bg-orange/5 border border-orange/30">
                  <input
                    type="datetime-local"
                    value={postDate}
                    onChange={(e) => setPostDate(e.target.value)}
                    disabled={busy}
                    aria-label="Post date and time"
                    className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange"
                  />
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {quickDates.map((q) => (
                      <button
                        key={q.label}
                        type="button"
                        onClick={() => setPostDate(q.value)}
                        disabled={busy}
                        className={`text-[11px] px-2 py-1 rounded-full transition active:scale-95 ${
                          postDate === q.value
                            ? 'bg-orange text-white'
                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-orange/10'
                        }`}
                      >
                        {q.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-2">
                    Change the post's displayed date. It will move to the correct
                    position in the feed.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Existing media */}
          {items.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Current media ({items.length})
                </label>
                {items.length > COLLAPSE_THRESHOLD && (
                  <button
                    type="button"
                    onClick={() => setExistingExpanded((v) => !v)}
                    className="text-[11px] text-orange hover:underline"
                  >
                    {existingExpanded
                      ? 'Show fewer'
                      : `Show all (${items.length})`}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {existingVisible.map((item, i) => (
                  <MediaTile
                    key={`${item.url}-${i}`}
                    item={item}
                    isCover={coverKey === `e-${i}`}
                    onSetCover={() => setCoverKey(`e-${i}`)}
                    onRemove={() => removeExistingItem(i)}
                    disabled={busy}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Add more photos / videos */}
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              Add more photos / videos
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={busy}
                className="flex-1 flex items-center justify-center gap-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50 transition active:scale-95"
              >
                <i className="fas fa-folder-open" aria-hidden="true" />
                Gallery
              </button>
              <button
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                disabled={busy}
                className="flex-1 flex items-center justify-center gap-2 bg-orange hover:bg-orange-dark text-white py-2.5 rounded-lg text-sm font-medium disabled:opacity-50 transition active:scale-95"
              >
                <i className="fas fa-camera" aria-hidden="true" />
                Camera
              </button>
            </div>
            <input
              ref={fileInputRef}
              id="post-media-input"
              type="file"
              accept="image/*,video/mp4,video/webm,video/quicktime"
              multiple
              onChange={handleFiles}
              disabled={busy}
              className="hidden"
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFiles}
              disabled={busy}
              className="hidden"
            />
          </div>

          {/* New file previews */}
          {newPreviews.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  New media ({newPreviews.length})
                </p>
                {newPreviews.length > COLLAPSE_THRESHOLD && (
                  <button
                    type="button"
                    onClick={() => setNewExpanded((v) => !v)}
                    className="text-[11px] text-orange hover:underline"
                  >
                    {newExpanded
                      ? 'Show fewer'
                      : `Show all (${newPreviews.length})`}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {newVisible.map((p, i) => (
                  <MediaTile
                    key={`${p.url}-${i}`}
                    item={p}
                    isCover={coverKey === `n-${i}`}
                    onSetCover={() => setCoverKey(`n-${i}`)}
                    onRemove={() => removeNewFile(i)}
                    disabled={busy}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Visibility */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              Visibility
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              {VISIBILITY_OPTIONS.map((opt) => {
                const active = visibility === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setVisibility(opt.value)}
                    disabled={busy}
                    aria-pressed={active}
                    title={opt.hint}
                    className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition disabled:opacity-50 active:scale-95 ${
                      active ? opt.activeCls : opt.inactiveCls
                    }`}
                  >
                    <i className={`fas ${opt.icon}`} aria-hidden="true" />
                    {opt.label}
                  </button>
                );
              })}
              <span className="text-[10px] text-gray-400 ml-1">
                {visibilityMeta.hint}
              </span>
            </div>
          </div>

          {/* Non-admin warning */}
          {!isAdmin && post.approved === true && (
            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg text-xs text-yellow-800 dark:text-yellow-200 flex items-start gap-2">
              <i className="fas fa-info-circle mt-0.5" aria-hidden="true" />
              <div>
                <strong>Your changes will require re-approval.</strong> Since
                you're not an admin, saving will put the post back into the
                pending queue.
              </div>
            </div>
          )}

          {/* Progress */}
          {busy && progress.total > 0 && (
            <div className="text-sm text-gray-600 dark:text-gray-300">
              <div
                className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden"
                role="progressbar"
                aria-valuenow={progress.done}
                aria-valuemin={0}
                aria-valuemax={progress.total}
              >
                <div
                  className="h-full bg-orange transition-all duration-300"
                  style={{
                    width: `${(progress.done / progress.total) * 100}%`,
                  }}
                />
              </div>
              <p className="text-xs mt-1 text-center">
                Uploading {progress.done} of {progress.total}…
              </p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div
              role="alert"
              className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 text-xs rounded-md p-2 whitespace-pre-line"
            >
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="px-4 sm:px-5 py-3 border-t dark:border-gray-700 flex justify-between items-center gap-2 flex-shrink-0 bg-white dark:bg-gray-800"
          style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
        >
          {!confirmDelete ? (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              disabled={busy}
              className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium flex items-center gap-2 disabled:opacity-50 transition active:scale-95"
            >
              <i className="fas fa-trash-alt" aria-hidden="true" />
              <span className="hidden sm:inline">Delete</span>
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-xs text-red-600 dark:text-red-400 font-medium">
                Sure?
              </span>
              <button
                type="button"
                onClick={handleDelete}
                disabled={busy}
                className="bg-red-500 hover:bg-red-600 text-white text-xs px-3 py-1.5 rounded font-medium disabled:opacity-50 active:scale-95"
              >
                Delete
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                disabled={busy}
                className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition disabled:opacity-50 active:scale-95"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave}
              className="px-4 py-2 text-sm font-medium text-white bg-orange hover:bg-orange-dark rounded-lg transition disabled:opacity-50 flex items-center gap-2 active:scale-95"
            >
              {busy ? (
                <>
                  <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                  Saving…
                </>
              ) : (
                <>
                  <i className="fas fa-save" aria-hidden="true" />
                  Save
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Link dialog */}
      {linkDialog && (
        <div
          className="fixed inset-0 z-[110] bg-black/50 flex items-center justify-center p-4"
          onClick={() => setLinkDialog(null)}
          style={{
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl p-4 w-full max-w-sm shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="font-semibold mb-2 text-sm text-gray-900 dark:text-white">
              {linkDialog.hasSelection ? 'Add link' : 'Insert link'}
            </h4>
            <input
              type="url"
              value={linkDialog.url}
              onChange={(e) =>
                setLinkDialog({ ...linkDialog, url: e.target.value })
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter') confirmLink();
                if (e.key === 'Escape') setLinkDialog(null);
              }}
              placeholder="https://example.com"
              autoFocus
              className="w-full p-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange"
            />
            <div className="flex justify-end gap-2 mt-3">
              <button
                type="button"
                onClick={() => setLinkDialog(null)}
                className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmLink}
                className="px-3 py-1.5 text-sm bg-orange text-white rounded hover:bg-orange-dark active:scale-95"
              >
                {linkDialog.url ? 'Add' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostEditor;